import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import {
  Download,
  Menu,
  X,
  Zap,
  Brain,
  Users,
  Shield,
  Sun,
  Moon,
  ChevronDown,
  Linkedin,
  Twitter,
  Code,
  ArrowRight,
  Terminal,
  Box,
  Cpu,
  Lock,
  Eye,
  Layers,
  Database,
  Server,
  CheckCircle,
  Sparkles,
  Target,
  TrendingUp,
  Globe,
} from 'lucide-react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ThemeContext } from './App.tsx';
import { useLocation } from 'react-router-dom'; // Import useLocation


const customStyles = `
  :root {
    --bg-color: #ffffff;
    --text-color: #111827;
    --text-color-secondary: #4B5563;
    --card-bg: #f9fafb;
    --card-border: #e5e7eb;
    --header-bg: rgba(255, 255, 255, 0.7);
    --hero-text-color: #111827;
    --gradient-text-from: #29B6F6;
    --gradient-text-to: #18FFFF;
    --brand-color-lime: #76FF03;
    --aws-hover-border: linear-gradient(135deg, #1565C0, #18FFFF);
    --footer-bg: #111827;
    --footer-text: #9CA3AF;

    /* New Section Backgrounds - Light Theme */
    --bg-main: #ffffff;
    --bg-off-white: #f9fafb;
    --bg-subtle-cream: #fefcf9;
    --bg-subtle-pink: #fdf6f8;

    /* New Stripe Shapes Colors - Light Theme */
    --stripe-shape-from: #6772e5;
    --stripe-shape-to: #87bbfd;
    --stripe-shape-opacity: 0.1;
  }

  [data-theme='dark'] {
    --bg-color: #030014;
    --text-color: #F9FAFB;
    --text-color-secondary: #9CA3AF;
    --card-bg: rgba(17, 24, 39, 0.5);
    --card-border: rgba(55, 65, 81, 0.5);
    --header-bg: rgba(0, 0, 0, 0.5);
    --hero-text-color: #F9FAFB;
    --gradient-text-from: #29B6F6;
    --gradient-text-to: #9C27B0;
    --brand-color-lime: #9C27B0;
    --aws-hover-border: linear-gradient(135deg, #1565C0, #000000);
    --footer-bg: #030014;
    --footer-text: #9CA3AF;

    /* New Section Backgrounds - Dark Theme */
    --bg-main: #030014;
    --bg-off-white: #0a0f1f;
    --bg-subtle-cream: #05021a;
    --bg-subtle-pink: #100512;

    /* New Stripe Shapes Colors - Dark Theme */
    --stripe-shape-from: #7a73ff;
    --stripe-shape-to: #32a8ff;
    --stripe-shape-opacity: 0.15;
  }

  html {
    scroll-behavior: smooth;
  }

  body, .theme-transition {
    background-color: var(--bg-color);
    color: var(--text-color);
    transition: background-color 0.3s ease, color 0.3s ease;
  }

  .section-transition {
    transition: background-color 0.3s ease;
  }

  .gradient-text-flow {
    background: linear-gradient(90deg,
      var(--gradient-text-from),
      var(--gradient-text-to),
      var(--gradient-text-from)
    );
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradient-flow 8s linear infinite;
  }

  @keyframes gradient-flow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  .glass-card {
    background: var(--card-bg);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid var(--card-border);
    transition: all 0.3s ease-in-out;
  }

  .aws-hover-effect {
    position: relative;
    background-clip: padding-box;
    border: 2px solid transparent;
    transition: all 0.3s ease;
  }

  .aws-hover-effect::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    margin: -2px;
    border-radius: inherit;
    background: var(--aws-hover-border);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .aws-hover-effect:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  [data-theme='dark'] .aws-hover-effect:hover {
    box-shadow: 0 10px 25px rgba(24, 255, 255, 0.1);
  }

  .aws-hover-effect:hover::before {
    opacity: 1;
  }

  .glow-button {
    position: relative;
    overflow: hidden;
    background: linear-gradient(90deg, #29B6F6, #18FFFF);
    transition: all 0.3s ease;
    color: #000;
  }

  [data-theme='dark'] .glow-button {
    background: linear-gradient(90deg, #18FFFF, #9C27B0);
    color: #000;
  }

  .glow-button:hover {
    box-shadow: 0 0 25px rgba(24, 255, 255, 0.5), 0 0 15px rgba(41, 182, 246, 0.4);
  }

  [data-theme='dark'] .glow-button:hover {
    box-shadow: 0 0 25px #9C27B0, 0 0 15px #18FFFF;
  }

  #agiCanvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    opacity: 0.6;
  }

  [data-theme='dark'] #agiCanvas {
    opacity: 0.8;
  }

  .logo-scroller {
    overflow: hidden;
    position: relative;
    -webkit-mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
    mask-image: linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%);
  }

  .logo-scroller-inner {
    display: flex;
    width: max-content;
    animation: scroll-left 30s linear infinite;
  }

  @keyframes scroll-left {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }

  .logo-scroller img {
    height: 60px;
    margin: 0 40px;
    filter: grayscale(1);
    opacity: 0.6;
    transition: all 0.3s ease;
  }

  .logo-scroller img:hover {
    filter: grayscale(0);
    opacity: 1;
  }

  [data-theme='dark'] .logo-scroller img {
    opacity: 0.8;
  }
  [data-theme='dark'] .logo-scroller img:hover {
    opacity: 1;
    filter: grayscale(0);
  }

  .geometric-bg {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
    pointer-events: none;
  }

  .geometric-shape {
    position: absolute;
    opacity: 0.1;
  }

  [data-theme='dark'] .geometric-shape {
    opacity: 0.15;
  }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(5deg); }
  }

  .floating {
    animation: float 6s ease-in-out infinite;
  }

  .stat-card {
    position: relative;
    overflow: hidden;
  }

  .stat-card::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
    transition: left 0.5s;
  }

  .stat-card:hover::after {
    left: 100%;
  }

  /* New Stripe Geometric Styles */
  .stripe-geometric-wrapper {
    position: relative;
    overflow: hidden; /* This is crucial */
  }

  .stripe-geometric-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    pointer-events: none;
    transform: skewY(-8deg); /* This angles the whole container */
  }

  .stripe-shape {
    position: absolute;
    right: 0;
    left: 0;
    height: 400px; /* Height of the shape */
    background-image: linear-gradient(120deg, var(--stripe-shape-from), var(--stripe-shape-to));
    opacity: var(--stripe-shape-opacity);
  }

  .stripe-shape-1 {
    top: -150px;
    transform: translateX(-20%);
  }

  .stripe-shape-2 {
    top: 100px;
    transform: translateX(20%);
  }

  .stripe-shape-3 {
    top: 350px;
    transform: translateX(-10%);
  }

  /* GPU Selection Modal Styles */
  .gpu-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(8px);
  }

  .gpu-modal-content {
    background: var(--card-bg);
    border-radius: 20px;
    padding: 2rem;
    max-width: 500px;
    width: 90%;
    border: 1px solid var(--card-border);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  }

  .gpu-option {
    display: flex;
    align-items: center;
    padding: 1rem;
    margin: 0.5rem 0;
    border-radius: 12px;
    border: 1px solid var(--card-border);
    transition: all 0.3s ease;
    cursor: pointer;
  }

  .gpu-option:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
    border-color: var(--gradient-text-from);
  }

  .gpu-option.disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .gpu-option.disabled:hover {
    transform: none;
    box-shadow: none;
    border-color: var(--card-border);
  }

  .gpu-logo {
    width: 40px;
    height: 40px;
    margin-right: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .gpu-logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .coming-soon-badge {
    background: linear-gradient(135deg, var(--gradient-text-from), var(--gradient-text-to));
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    margin-left: auto;
  }
`;

const AGICanvas = ({ theme }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId;
    const time = { current: 0 };

    const quantumParticles = [];
    const dataStreams = [];
    const holographicCubes = [];
    const energyWaves = [];
    const digitalRings = [];

    const resizeCanvas = () => {
      if (canvas) {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();

    const getParticleColor = (hue, alpha) => {
      const newHue = hue + 40;
      return theme === 'light'
        ? `hsla(${newHue}, 70%, 50%, ${alpha})`
        : `hsla(${newHue}, 80%, 70%, ${alpha})`;
    };

    const getStreamColor = (hue, alpha) => {
      const newHue = 195;
      return theme === 'light'
        ? `hsla(${newHue}, 70%, 45%, ${alpha})`
        : `hsla(${newHue}, 80%, 70%, ${alpha})`;
    };

    const getCubeColor = (hue, alpha) => {
      const newHue = 220;
      return theme === 'light'
        ? `hsla(${newHue}, 60%, 55%, ${alpha})`
        : `hsla(${newHue}, 70%, 60%, ${alpha})`;
    };

    const getWaveColor = (hue, alpha) => {
      const newHue = 180;
      return theme === 'light'
        ? `hsla(${newHue}, 70%, 50%, ${alpha})`
        : `hsla(${newHue}, 80%, 60%, ${alpha})`;
    };

    const getRingColor = (hue, alpha) => {
      const newHue = 90;
      return theme === 'light'
        ? `hsla(${newHue}, 60%, 50%, ${alpha})`
        : `hsla(${newHue}, 70%, 60%, ${alpha})`;
    };

    const getLineColor = (alpha) => {
      return theme === 'light'
        ? `rgba(41, 182, 246, ${alpha})`
        : `rgba(24, 255, 255, ${alpha})`;
    };

    class QuantumParticle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = (Math.random() - 0.5) * 1.5;
        this.speedY = (Math.random() - 0.5) * 1.5;
        this.phase = Math.random() * Math.PI * 2;
        this.frequency = Math.random() * 0.02 + 0.01;
        this.hue = Math.random() * 60 + 170;
      }

      update() {
        this.x += this.speedX + Math.sin(time.current * this.frequency + this.phase) * 0.5;
        this.y += this.speedY + Math.cos(time.current * this.frequency + this.phase) * 0.5;

        if (this.x < 0) this.x = canvas.width;
        if (this.x > canvas.width) this.x = 0;
        if (this.y < 0) this.y = canvas.height;
        if (this.y > canvas.height) this.y = 0;
      }

      draw() {
        const pulseSize = this.size + Math.sin(time.current * 0.05 + this.phase) * 1;
        const alpha = 0.5 + Math.sin(time.current * 0.03 + this.phase) * 0.3;

        const color = getParticleColor(this.hue, alpha);
        const shadowColor = getParticleColor(this.hue, alpha * 0.8);

        ctx.shadowBlur = 10;
        ctx.shadowColor = shadowColor;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, pulseSize, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = color.replace(/[\d.]+\)$/g, `${alpha * 0.5})`);
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.arc(this.x, this.y, pulseSize * 2, 0, Math.PI * 2);
        ctx.stroke();

        ctx.shadowBlur = 0;
      }
    }

    class DataStream {
        constructor() {
            this.reset();
            this.segments = [];
            for (let i = 0; i < 8; i++) {
                this.segments.push({
                    x: this.x - i * 15,
                    y: this.y + Math.sin(i * 0.5) * 10,
                    opacity: 1 - i * 0.12
                });
            }
        }
        reset() {
            this.x = -50;
            this.y = Math.random() * canvas.height;
            this.speedX = Math.random() * 3 + 2;
            this.speedY = (Math.random() - 0.5) * 0.5;
            this.hue = Math.random() * 60 + 170;
        }
        update() {
            this.x += this.speedX;
            this.y += this.speedY + Math.sin(time.current * 0.01 + this.x * 0.001) * 0.2;
            for (let i = this.segments.length - 1; i > 0; i--) {
                this.segments[i].x = this.segments[i - 1].x;
                this.segments[i].y = this.segments[i - 1].y;
            }
            this.segments[0].x = this.x;
            this.segments[0].y = this.y;
            if (this.x > canvas.width + 100) {
                this.reset();
            }
        }
        draw() {
            this.segments.forEach((segment, i) => {
                const size = 4 - i * 0.3;
                const alpha = segment.opacity * (0.8 - i * 0.1);
                const color = getStreamColor(this.hue, alpha);

                ctx.fillStyle = color;
                ctx.shadowBlur = 8;
                ctx.shadowColor = color;

                ctx.beginPath();
                ctx.arc(segment.x, segment.y, size, 0, Math.PI * 2);
                ctx.fill();

                if (i > 0) {
                    ctx.strokeStyle = color.replace(/[\d.]+\)$/g, `${alpha * 0.3})`);
                    ctx.lineWidth = 1;
                    ctx.beginPath();
                    ctx.moveTo(this.segments[i - 1].x, this.segments[i - 1].y);
                    ctx.lineTo(segment.x, segment.y);
                    ctx.stroke();
                }
            });
            ctx.shadowBlur = 0;
        }
    }

    class HolographicCube {
         constructor() {
            this.x = Math.random() * canvas.width;
            this.y = Math.random() * canvas.height;
            this.size = Math.random() * 30 + 20;
            this.rotationX = 0; this.rotationY = 0; this.rotationZ = 0;
            this.rotSpeedX = (Math.random() - 0.5) * 0.02;
            this.rotSpeedY = (Math.random() - 0.5) * 0.02;
            this.rotSpeedZ = (Math.random() - 0.5) * 0.02;
            this.phase = Math.random() * Math.PI * 2;
            this.floatSpeed = Math.random() * 0.01 + 0.005;
            this.originalY = this.y;
            this.hue = Math.random() * 60 + 260;
        }
        update() {
            this.rotationX += this.rotSpeedX;
            this.rotationY += this.rotSpeedY;
            this.rotationZ += this.rotSpeedZ;
            this.y = this.originalY + Math.sin(time.current * this.floatSpeed + this.phase) * 20;
        }
        getCubeVertices() {
            const s = this.size / 2;
            const vertices3D = [
                [-s, -s, -s], [s, -s, -s], [s, s, -s], [-s, s, -s],
                [-s, -s, s], [s, -s, s], [s, s, s], [-s, s, s]
            ];
            return vertices3D.map(([x, y, z]) => {
                let [rx, ry, rz] = [x, y, z];
                const cosX = Math.cos(this.rotationX); const sinX = Math.sin(this.rotationX);
                [ry, rz] = [ry * cosX - rz * sinX, ry * sinX + rz * cosX];
                const cosY = Math.cos(this.rotationY); const sinY = Math.sin(this.rotationY);
                [rx, rz] = [rx * cosY + rz * sinY, -rx * sinY + rz * cosY];
                const cosZ = Math.cos(this.rotationZ); const sinZ = Math.sin(this.rotationZ);
                [rx, ry] = [rx * cosZ - ry * sinZ, rx * sinZ + ry * cosZ];
                const perspective = 300 / (300 + rz);
                return { x: rx * perspective, y: ry * perspective };
            });
        }
        draw() {
            ctx.save();
            ctx.translate(this.x, this.y);

            const alpha = 0.4 + Math.sin(time.current * 0.02 + this.phase) * 0.2;
            const glitchOffset = Math.sin(time.current * 0.1) * 1.5;
            ctx.translate(glitchOffset, 0);

            const vertices = this.getCubeVertices();
            const edges = [
                [0,1],[1,2],[2,3],[3,0], [4,5],[5,6],[6,7],[7,4], [0,4],[1,5],[2,6],[3,7]
            ];

            const color = getCubeColor(this.hue, alpha);
            const faceColor = getCubeColor(this.hue, alpha * 0.15);

            ctx.strokeStyle = color;
            ctx.lineWidth = 1.5;
            ctx.shadowBlur = 10;
            ctx.shadowColor = color;

            edges.forEach(edge => {
                const start = vertices[edge[0]];
                const end = vertices[edge[1]];
                ctx.beginPath();
                ctx.moveTo(start.x, start.y);
                ctx.lineTo(end.x, end.y);
                ctx.stroke();
            });

            ctx.fillStyle = faceColor;
            const faces = [[0,1,2,3], [4,5,6,7], [0,1,5,4], [2,3,7,6], [0,3,7,4], [1,2,6,5]];
            faces.forEach(face => {
                ctx.beginPath();
                ctx.moveTo(vertices[face[0]].x, vertices[face[0]].y);
                for (let i = 1; i < face.length; i++) {
                    ctx.lineTo(vertices[face[i]].x, vertices[face[i]].y);
                }
                ctx.closePath();
                ctx.fill();
            });

            ctx.shadowBlur = 0;
            ctx.restore();
        }
    }

    class EnergyWave {
        constructor() {
            this.centerX = Math.random() * canvas.width;
            this.centerY = Math.random() * canvas.height;
            this.radius = 0;
            this.maxRadius = Math.random() * 200 + 100;
            this.speed = Math.random() * 2 + 1;
            this.hue = Math.random() * 60 + 170;
            this.opacity = 1;
            this.thickness = Math.random() * 3 + 1;
        }
        update() {
            this.radius += this.speed;
            this.opacity = 1 - (this.radius / this.maxRadius);
            if (this.radius > this.maxRadius) {
                this.radius = 0;
                this.centerX = Math.random() * canvas.width;
                this.centerY = Math.random() * canvas.height;
                this.opacity = 1;
            }
        }
        draw() {
            if (this.opacity <= 0) return;
            const color = getWaveColor(this.hue, this.opacity);

            ctx.strokeStyle = color;
            ctx.lineWidth = this.thickness;
            ctx.shadowBlur = 15;
            ctx.shadowColor = color.replace(/[\d.]+\)$/g, `${this.opacity * 0.5})`);
            ctx.beginPath();
            ctx.arc(this.centerX, this.centerY, this.radius, 0, Math.PI * 2);
            ctx.stroke();
            ctx.shadowBlur = 0;
        }
    }

    class DigitalRing {
        constructor() {
            this.x = Math.random() * canvas.width;
            this.y = Math.random() * canvas.height;
            this.innerRadius = Math.random() * 20 + 10;
            this.outerRadius = this.innerRadius + Math.random() * 15 + 5;
            this.rotation = 0;
            this.rotSpeed = (Math.random() - 0.5) * 0.03;
            this.pulsePhase = Math.random() * Math.PI * 2;
            this.hue = Math.random() * 60 + 80;
            this.segments = Math.floor(Math.random() * 16) + 8;
        }
        update() {
            this.rotation += this.rotSpeed;
            this.x += Math.sin(time.current * 0.001 + this.pulsePhase) * 0.5;
            this.y += Math.cos(time.current * 0.001 + this.pulsePhase) * 0.3;
            if (this.x < -50) this.x = canvas.width + 50;
            if (this.x > canvas.width + 50) this.x = -50;
            if (this.y < -50) this.y = canvas.height + 50;
            if (this.y > canvas.height + 50) this.y = -50;
        }
        draw() {
            ctx.save();
            ctx.translate(this.x, this.y);
            ctx.rotate(this.rotation);

            const pulse = 0.7 + Math.sin(time.current * 0.05 + this.pulsePhase) * 0.3;
            const alpha = 0.5 * pulse;

            for (let i = 0; i < this.segments; i++) {
                const angle = (i / this.segments) * Math.PI * 2;
                const nextAngle = ((i + 1) / this.segments) * Math.PI * 2;
                const segmentAlpha = alpha * (0.5 + Math.sin(time.current * 0.1 + i) * 0.5);

                const color = getRingColor(this.hue, segmentAlpha);
                const strokeColor = getRingColor(this.hue, segmentAlpha * 1.2);

                ctx.fillStyle = color;
                ctx.strokeStyle = strokeColor;
                ctx.lineWidth = 1;

                ctx.beginPath();
                ctx.arc(0, 0, this.outerRadius * pulse, angle, nextAngle);
                ctx.arc(0, 0, this.innerRadius * pulse, nextAngle, angle, true);
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
            }
            ctx.restore();
        }
    }

    const initializeElements = () => {
      quantumParticles.length = 0;
      dataStreams.length = 0;
      holographicCubes.length = 0;
      energyWaves.length = 0;
      digitalRings.length = 0;
      const particleCount = window.innerWidth > 768 ? 60 : 30;
      const streamCount = window.innerWidth > 768 ? 10 : 5;
      const cubeCount = window.innerWidth > 768 ? 6 : 3;
      const waveCount = window.innerWidth > 768 ? 5 : 2;
      const ringCount = window.innerWidth > 768 ? 8 : 4;
      for (let i = 0; i < particleCount; i++) quantumParticles.push(new QuantumParticle());
      for (let i = 0; i < streamCount; i++) {
        const stream = new DataStream();
        stream.x = Math.random() * canvas.width;
        dataStreams.push(stream);
      }
      for (let i = 0; i < cubeCount; i++) holographicCubes.push(new HolographicCube());
      for (let i = 0; i < waveCount; i++) energyWaves.push(new EnergyWave());
      for (let i = 0; i < ringCount; i++) digitalRings.push(new DigitalRing());
    };

    initializeElements();
    window.addEventListener('resize', initializeElements);

    const animate = () => {
      time.current += 1;

      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height)
      );

      if (theme === 'light') {
        gradient.addColorStop(0, 'rgba(239, 246, 255, 0.9)');
        gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.95)');
        gradient.addColorStop(1, 'rgba(255, 255, 255, 1)');
      } else {
        gradient.addColorStop(0, 'rgba(5, 0, 15, 0.95)');
        gradient.addColorStop(0.5, 'rgba(10, 0, 30, 0.98)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 1)');
      }

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      quantumParticles.forEach(particle => { particle.update(); particle.draw(); });
      dataStreams.forEach(stream => { stream.update(); stream.draw(); });
      energyWaves.forEach(wave => { wave.update(); wave.draw(); });
      digitalRings.forEach(ring => { ring.update(); ring.draw(); });
      holographicCubes.forEach(cube => { cube.update(); cube.draw(); });

      for (let i = 0; i < quantumParticles.length; i++) {
        for (let j = i + 1; j < quantumParticles.length; j++) {
          const dx = quantumParticles[i].x - quantumParticles[j].x;
          const dy = quantumParticles[i].y - quantumParticles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            const alpha = (1 - distance / 100) * 0.2;
            ctx.strokeStyle = getLineColor(alpha);
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(quantumParticles[i].x, quantumParticles[i].y);
            ctx.lineTo(quantumParticles[j].x, quantumParticles[j].y);
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('resize', initializeElements);
    };
  }, [theme]);

  return <canvas id="agiCanvas" ref={canvasRef}></canvas>;
};

const useTypewriter = (text, speed = 50) => {
  const [displayText, setDisplayText] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);

  useEffect(() => {
    setDisplayText('');
    setIsTypingComplete(false);
    let i = 0;
    const startDelay = setTimeout(() => {
      const typingInterval = setInterval(() => {
        if (i < text.length) {
          setDisplayText(text.slice(0, i + 1));
          i++;
        } else {
          clearInterval(typingInterval);
          setIsTypingComplete(true);
        }
      }, speed);
      return () => clearInterval(typingInterval);
    }, 500);

    return () => {
      clearTimeout(startDelay);
    };
  }, [text, speed]);

  return { displayText, isTypingComplete };
};

const AnimatedSection = ({ children, className = '', id, style = {} }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.05, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section
      id={id}
      ref={ref}
      className={`py-20 md:py-28 ${className}`}
      style={style}
      initial="hidden"
      animate={controls}
      variants={{
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.2 } },
        hidden: { opacity: 0, y: 50 },
      }}
    >
      {children}
    </motion.section>
  );
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const NavDropdown = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button className="flex items-center space-x-1 text-[var(--text-color-secondary)] hover:text-[var(--text-color)] transition duration-300">
        <span>{title}</span>
        <ChevronDown size={16} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 mt-2 w-64 glass-card rounded-lg shadow-xl p-4 z-50"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DropdownItem = ({ href, title, description }) => (
  <a
    href={href}
    className="block p-3 rounded-md hover:bg-[var(--card-border)] transition duration-200"
    onClick={(e) => {
      if (href === "#") {
        e.preventDefault();
        // Simple message box instead of alert
        const msgBox = document.createElement('div');
        msgBox.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background-color:var(--card-bg);color:var(--text-color);padding:12px 20px;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.1);z-index:1000;font-family:Inter,sans-serif;font-size:14px;';
        msgBox.textContent = 'Coming Soon!';
        document.body.appendChild(msgBox);
        setTimeout(() => {
          msgBox.style.transition = 'opacity 0.3s ease';
          msgBox.style.opacity = '0';
          setTimeout(() => document.body.removeChild(msgBox), 300);
        }, 2000);
      }
    }}
  >
    <p className="font-semibold text-[var(--text-color)]">{title}</p>
    <p className="text-sm text-[var(--text-color-secondary)]">{description}</p>
  </a>
);


const LogoScroller = () => {
  const logos = [
    { name: "Nvidia Inception", url: "https://placehold.co/150x60/transparent/888888?text=NVIDIA+Inception" },
    { name: "Google for Startups", url: "https://placehold.co/150x60/transparent/888888?text=Google" },
    { name: "AWS Activate", url: "https://placehold.co/150x60/transparent/888888?text=AWS" },
    { name: "Nvidia DGX Innovation", url: "https://placehold.co/150x60/transparent/888888?text=NVIDIA+DGX+Innovation" },
    { name: "Cloudflare", url: "https://placehold.co/150x60/transparent/888888?text=Cloudflare" },
  ];

  const allLogos = [...logos, ...logos ];

  return (
    <AnimatedSection id="partners" className="section-transition" style={{ backgroundColor: 'var(--bg-main)' }}>
      <div className="container mx-auto px-4 sm:px-6">
        <motion.h3 variants={itemVariants} className="text-center text-3xl font-semibold text-[var(--text-color-secondary)] mb-12">
          Supported by today's innovators
        </motion.h3>
        <motion.div className="logo-scroller" variants={itemVariants}>
          <div className="logo-scroller-inner">
            {allLogos.map((logo, index) => (
              <img key={index} src={logo.url} alt={logo.name} className="h-12 md:h-24" />
            ))}
          </div>
        </motion.div>
      </div>
    </AnimatedSection>
  );
};



// GPU Selection Modal Component
const GPUSelectionModal = ({ isOpen, onClose, onGPUSelected }) => {
  const gpuOptions = [
    {
      id: 'nvidia',
      name: 'NVIDIA',
      available: true,
      downloadUrl: 'https://huggingface.co/dahwinsingularity/fluxfp8/resolve/main/QueenDahyun_Engine_Test.exe'
    },
    {
      id: 'amd',
      name: 'AMD',
      available: false
    },
    {
      id: 'intel',
      name: 'Intel',
      available: false
    }
  ];

  const handleGPUClick = (gpu) => {
    if (gpu.available) {
      // Direct download like the original code
      const link = document.createElement('a');
      link.href = gpu.downloadUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="gpu-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="gpu-modal-content"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-[var(--text-color)] mb-2">
            Select Your GPU
          </h3>
          <p className="text-[var(--text-color-secondary)]">
            Choose your graphics card manufacturer to get the optimized version
          </p>
        </div>

        <div className="space-y-3">
          {gpuOptions.map((gpu) => (
            <motion.div
              key={gpu.id}
              className={`gpu-option ${!gpu.available ? 'disabled' : ''}`}
              whileHover={gpu.available ? { scale: 1.02 } : {}}
              whileTap={gpu.available ? { scale: 0.98 } : {}}
              onClick={() => handleGPUClick(gpu)}
            >
              <div className="flex-1">
                <h4 className="font-semibold text-[var(--text-color)]">{gpu.name}</h4>
                <p className="text-sm text-[var(--text-color-secondary)]">
                  {gpu.available ? 'Optimized for performance' : 'Support coming soon'}
                </p>
              </div>
              {!gpu.available && (
                <span className="coming-soon-badge">Coming Soon</span>
              )}
            </motion.div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-6 py-3 px-4 text-[var(--text-color-secondary)] hover:text-[var(--text-color)] transition-colors border border-[var(--card-border)] rounded-lg hover:border-[var(--text-color-secondary)]"
        >
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
};



const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);
  const location = useLocation();
  const [showGPUModal, setShowGPUModal] = useState(false);

  // =================================================================
  // == THIS IS THE FIX: Scroll to hash element when page loads      ==
  // =================================================================
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        // Use a timeout to ensure the page has finished rendering
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]); // Rerun this effect when the location changes
  // =================================================================

  const heroText = "Autonomous AI Agent: QueenDahyun";
  const { displayText: typedHeroText, isTypingComplete: heroTyped } = useTypewriter(heroText, 70);
  const handleGetStarted = () => {
    window.open('/signup', '_blank');
  };

  const navLinks = [
    { href: "/Team", label: "Team" },
    { href: "#download", label: "Download" },
  ];

  return (
    <>
      <style>{customStyles}</style>
      <div className="min-h-screen text-[var(--text-color)] overflow-x-hidden theme-transition" style={{ backgroundColor: 'var(--bg-main)' }}>
        <AGICanvas theme={theme} />

        <div className="relative z-10">
          <motion.header
            className="bg-[var(--header-bg)] backdrop-filter backdrop-blur-md fixed w-full z-50 shadow-md border-b border-[var(--card-border)] theme-transition"
            initial={{ y: -100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <nav className="container mx-auto px-4 sm:px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-8">
                  <motion.div
                    className="flex items-center space-x-2"
                    whileHover={{ scale: 1.05 }}
                  >
                    <img src={require('./assets/dahyun_logo.png')} alt="QueenDahyun Logo" className="w-10 h-10 object-contain" />
                    <span className="text-xl font-bold text-[var(--text-color)]">
                      QueenDahyun AGI
                    </span>
                  </motion.div>
                  <div className="hidden md:flex space-x-6">
                    <NavDropdown title="Solution">
                      <DropdownItem href="#" title="For Developers" description="Tools and APIs to build." />
                      <DropdownItem href="#" title="For Researchers" description="Access state-of-the-art models." />
                      <DropdownItem href="#" title="For Enterprise" description="Scale your AI infrastructure." />
                    </NavDropdown>
                    <NavDropdown title="Product">
                      <DropdownItem href="/transformer-engine" title="Transformer Engine" description="Our high-performance local runtime." />
                      <DropdownItem href="/computer-brain-interface" title="SoulDubu VLM" description="The brain for your computer." />
                    </NavDropdown>
                    <NavDropdown title="Research">
                      <DropdownItem href="/research" title="Continuous Learning" description="Read our latest papers." />
                      <DropdownItem href="/robotics" title="SoulDubu Robotics" description="Our advanced robotics division." />
                      <DropdownItem href="/Team" title="Our Team" description="Meet the minds behind the magic." />
                    </NavDropdown>
                    <NavDropdown title="Blog">
                      <DropdownItem href="/blog" title="All Posts" description="News and updates." />
                      <DropdownItem href="#" title="Engineering" description="Deep dives from our team." />
                    </NavDropdown>
                    <NavDropdown title="Partner">
                       <DropdownItem href="#" title="Become a Partner" description="Join our partner network." />
                    </NavDropdown>
                    <NavDropdown title="API">
                       <DropdownItem href="#" title="Documentation" description="Explore our API endpoints." />
                    </NavDropdown>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="hidden md:flex space-x-6">
                    {navLinks.map((link) => (
                      <motion.a
                        key={link.href}
                        href={link.href}
                        className="text-[var(--text-color-secondary)] hover:text-[var(--text-color)] transition duration-300"
                        whileHover={{ y: -2 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        {link.label}
                      </motion.a>
                    ))}
                  </div>
                  <motion.button
                    onClick={toggleTheme}
                    className="p-2 rounded-full hover:bg-[var(--card-border)] text-[var(--text-color-secondary)] transition-colors"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <AnimatePresence mode="wait">
                      {theme === 'light' ? (
                        <motion.div key="moon" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                          <Moon size={20} />
                        </motion.div>
                      ) : (
                         <motion.div key="sun" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                          <Sun size={20} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                  <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-[var(--text-color-secondary)] hover:text-[var(--text-color)] z-50">
                    <AnimatePresence mode="wait">
                      {isMenuOpen ? (
                        <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}>
                          <X size={28} />
                        </motion.div>
                      ) : (
                        <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}>
                          <Menu size={28} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </button>
                </div>
              </div>
            </nav>
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  className="md:hidden bg-[var(--header-bg)] backdrop-filter backdrop-blur-lg absolute top-full left-0 right-0 shadow-xl border-t border-[var(--card-border)]"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                >
                  {navLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="block py-3 px-6 text-[var(--text-color)] hover:bg-[var(--card-border)] transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.header>

          <section id="home" className="pt-32 pb-24 md:pt-48 md:pb-32 min-h-screen flex flex-col justify-center items-center text-center relative">
            <div className="container mx-auto px-4 sm:px-6">
              <motion.h1
                  className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black mb-6 gradient-text-flow"
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.2, duration: 0.8 }}
              >
                The State Of The Art
              </motion.h1>
              <motion.p
                  className="text-xl sm:text-2xl md:text-3xl mb-10 text-[var(--hero-text-color)]"
                  style={{ minHeight: '2.5em' }}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: 0.5, duration: 0.8 }}
              >
                {typedHeroText || "Autonomous AI Agent: QueenDahyun"}
                {typedHeroText && !heroTyped && <span className="animate-pulse ml-1">|</span>}
              </motion.p>
              <motion.button
                  onClick={handleGetStarted}
                  className="text-black py-3 px-8 sm:py-4 sm:px-10 rounded-full text-lg sm:text-xl font-semibold glow-button shadow-xl"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, type: "spring", stiffness: 200, damping: 15 }}
              >
                Get Started : SignIn
              </motion.button>
            </div>
          </section>

          {/* DETAILED INNOVATION SECTION */}
          <AnimatedSection id="solutions" className="section-transition" style={{ backgroundColor: 'var(--bg-off-white)' }}>
            <div className="container mx-auto px-4 sm:px-6">
              <motion.h2 variants={itemVariants} className="text-4xl sm:text-5xl font-bold text-center mb-6 gradient-text-flow">
                Built for Innovation
              </motion.h2>
              <motion.p variants={itemVariants} className="text-center text-[var(--text-color-secondary)] text-lg mb-20 max-w-3xl mx-auto">
                QueenDahyun represents a paradigm shift in artificial intelligence—a complete autonomous system that operates as the cognitive layer for your entire digital workspace.
              </motion.p>

              <div className="space-y-32">
                {/* Computer Brain Interface - DETAILED */}
                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 md:p-12 rounded-2xl shadow-2xl">
                  <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div>
                      <div className="flex items-center mb-6">
                        <Brain size={48} className="text-[var(--gradient-text-from)] mr-4"/>
                        <h3 className="text-3xl md:text-4xl font-bold text-[var(--text-color)]">Computer Brain Interface</h3>
                      </div>
                      <p className="text-[var(--text-color-secondary)] text-lg leading-relaxed mb-6">
                        Our flagship innovation, <span className="font-semibold text-[var(--gradient-text-from)]">SoulDubu</span>, is an advanced vision-language model that fundamentally transforms how humans interact with computers. Unlike traditional chatbots or browser-based agents, SoulDubu operates as a true <span className="font-semibold">end-to-end autonomous system</span> capable of perceiving, understanding, and acting within any digital environment.
                      </p>
                      <p className="text-[var(--text-color-secondary)] text-lg leading-relaxed mb-6">
                        Through sophisticated <span className="font-semibold">computer vision</span>, <span className="font-semibold">natural language processing</span>, and <span className="font-semibold">GUI navigation capabilities</span>, SoulDubu can perform any task that a human can perform on a computer—from complex software development and scientific research to data analysis and creative work.
                      </p>
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <Eye className="text-[var(--brand-color-lime)] mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Advanced Vision System</h4>
                            <p className="text-[var(--text-color-secondary)]">Perceives and understands screen content with human-level comprehension, identifying UI elements, text, images, and contextual relationships.</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <Terminal className="text-[var(--brand-color-lime)] mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Universal Software Compatibility</h4>
                            <p className="text-[var(--text-color-secondary)]">Works seamlessly with any application—IDEs, scientific tools, design software, operating system utilities—without requiring special integrations or APIs.</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <Layers className="text-[var(--brand-color-lime)] mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Autonomous Task Execution</h4>
                            <p className="text-[var(--text-color-secondary)]">Plans, executes, and validates complex multi-step workflows, operating as a true virtual partner that can work independently or collaboratively.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <img
                        src={require('./assets/souldubu.jpeg')}
                        alt="Computer Brain Interface Visualization"
                        className="rounded-xl shadow-2xl w-full"
                      />
                      <div className="absolute -bottom-6 -right-6 glass-card p-4 rounded-lg shadow-xl">
                        <p className="text-sm font-semibold text-[var(--text-color)]">Model: SoulDubu</p>
                        <p className="text-xs text-[var(--text-color-secondary)]">Vision-Language-Action</p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Local Inference Engine - DETAILED */}
                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 md:p-12 rounded-2xl shadow-2xl">
                  <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div className="order-2 md:order-1 relative">
                      <img
                        src={require('./assets/engine.jpeg')}
                        alt="Local Inference Engine"
                        className="rounded-xl shadow-2xl w-full"
                      />
                      <div className="absolute -top-6 -left-6 glass-card p-4 rounded-lg shadow-xl">
                        <div className="flex items-center">
                          <Zap className="text-[var(--brand-color-lime)] mr-2" size={20}/>
                          <p className="text-sm font-semibold text-[var(--text-color)]">High-Performance Runtime</p>
                        </div>
                      </div>
                    </div>
                    <div className="order-1 md:order-2">
                      <div className="flex items-center mb-6">
                        <Cpu size={48} className="text-[var(--brand-color-lime)] mr-4"/>
                        <h3 className="text-3xl md:text-4xl font-bold text-[var(--text-color)]">Local Inference Engine</h3>
                      </div>
                      <p className="text-[var(--text-color-secondary)] text-lg leading-relaxed mb-6">
                        Privacy and performance meet in our proprietary <span className="font-semibold">local inference runtime</span>—a cutting-edge execution engine designed from the ground up for maximum efficiency and zero dependency on cloud connectivity. Your data never leaves your device, yet you experience enterprise-grade AI capabilities.
                      </p>
                      <p className="text-[var(--text-color-secondary)] text-lg leading-relaxed mb-6">
                        Our engine doesn't just run our <span className="font-semibold text-[var(--brand-color-lime)]">mini open-source models</span>—it's a universal runtime that supports any compatible open-source model from the community. Whether you're running inference on consumer hardware or high-end workstations, our optimizations ensure maximum throughput and minimal latency.
                      </p>
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <Server className="text-[var(--gradient-text-from)] mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Zero Cloud Dependency</h4>
                            <p className="text-[var(--text-color-secondary)]">Complete offline operation—no internet connection required. Your sensitive data and proprietary work remain entirely on your local machine.</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <Database className="text-[var(--gradient-text-from)] mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Universal Model Support</h4>
                            <p className="text-[var(--text-color-secondary)]">Run any compatible open-source model from Hugging Face, custom fine-tuned models, or our optimized mini models—all through one unified runtime.</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <Zap className="text-[var(--gradient-text-from)] mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Optimized Performance</h4>
                            <p className="text-[var(--text-color-secondary)]">Hardware-accelerated inference with automatic optimization for your CPU, GPU, and NPU—delivering production-grade speed on consumer devices.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Safe & Secure - DETAILED */}
                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 md:p-12 rounded-2xl shadow-2xl">
                  <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div>
                      <div className="flex items-center mb-6">
                        <Shield size={48} className="text-pink-500 mr-4"/>
                        <h3 className="text-3xl md:text-4xl font-bold text-[var(--text-color)]">Safe & Secure</h3>
                      </div>
                      <p className="text-[var(--text-color-secondary)] text-lg leading-relaxed mb-6">
                        Security isn't an afterthought—it's woven into the very fabric of QueenDahyun. Our AI operates with a <span className="font-semibold">security-first personality</span>, designed to be helpful yet cautious, powerful yet safe. Every action requires explicit user permission, and our multi-layered safety systems ensure your digital workspace remains protected.
                      </p>
                      <p className="text-[var(--text-color-secondary)] text-lg leading-relaxed mb-6">
                        We've implemented <span className="font-semibold">behavioral constraints</span> at the model level, <span className="font-semibold">permission systems</span> at the application level, and <span className="font-semibold">audit trails</span> at the system level. The result is an AI that's both autonomous and accountable—capable of independent operation while respecting boundaries and requiring consent for sensitive actions.
                      </p>
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <Lock className="text-pink-500 mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Permission-Based Architecture</h4>
                            <p className="text-[var(--text-color-secondary)]">SoulDubu never modifies, deletes, or moves files without explicit user authorization. All destructive operations require interactive confirmation.</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <CheckCircle className="text-pink-500 mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Behavioral Safety Constraints</h4>
                            <p className="text-[var(--text-color-secondary)]">Built-in safety mechanisms prevent accidental harm—the AI understands context, assesses risk, and defaults to conservative actions when uncertainty exists.</p>
                          </div>
                        </div>
                        <div className="flex items-start">
                          <Eye className="text-pink-500 mr-3 mt-1 flex-shrink-0" size={24}/>
                          <div>
                            <h4 className="font-semibold text-[var(--text-color)] mb-1">Complete Transparency</h4>
                            <p className="text-[var(--text-color-secondary)]">Every action is logged and auditable. Review what the AI has done, understand its decision-making process, and maintain full control over your digital environment.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="relative">
                      <img
                        src={require('./assets/secure.jpeg')}
                        alt="Security Architecture"
                        className="rounded-xl shadow-2xl w-full"
                      />
                      <div className="absolute -bottom-6 -right-6 glass-card p-4 rounded-lg shadow-xl">
                        <div className="flex items-center">
                          <Shield className="text-pink-500 mr-2" size={20}/>
                          <p className="text-sm font-semibold text-[var(--text-color)]">Security-First Design</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </AnimatedSection>

          {/* Technology Showcase Section */}
          <AnimatedSection id="technology" className="section-transition" style={{ backgroundColor: 'var(--bg-main)' }}>
            <div className="container mx-auto px-4 sm:px-6">
              <motion.h2 variants={itemVariants} className="text-4xl sm:text-5xl font-bold text-center mb-6 gradient-text-flow">
                Advanced Technology Stack
              </motion.h2>
              <motion.p variants={itemVariants} className="text-center text-[var(--text-color-secondary)] text-lg mb-16 max-w-3xl mx-auto">
                Powered by cutting-edge research and engineered for real-world deployment
              </motion.p>

              <div className="grid md:grid-cols-3 gap-8">
                <motion.div variants={itemVariants} className="glass-card p-6 rounded-xl stat-card">
                  <Sparkles className="text-[var(--gradient-text-from)] mb-4" size={40}/>
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-2">Vision-Language Model</h3>
                  <p className="text-[var(--text-color-secondary)]">State-of-the-art multimodal architecture combining visual understanding with natural language reasoning for seamless human-computer interaction.</p>
                </motion.div>

                <motion.div variants={itemVariants} className="glass-card p-6 rounded-xl stat-card">
                  <Target className="text-[var(--brand-color-lime)] mb-4" size={40}/>
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-2">Precision GUI Control</h3>
                  <p className="text-[var(--text-color-secondary)]">Sub-pixel accuracy in UI element detection and interaction, enabling reliable automation of even the most complex software workflows.</p>
                </motion.div>

                <motion.div variants={itemVariants} className="glass-card p-6 rounded-xl stat-card">
                  <TrendingUp className="text-pink-500 mb-4" size={40}/>
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-2">Continuous Learning (Beta)</h3>
                  <p className="text-[var(--text-color-secondary)]">Adapts to your workflow patterns and preferences over time, becoming more efficient and personalized with each interaction.</p>
                </motion.div>
              </div>
            </div>
          </AnimatedSection>

          {/* FEATURES SECTION WITH STRIPE GEOMETRY */}
          <AnimatedSection
            id="features"
            className="stripe-geometric-wrapper section-transition"
            style={{ backgroundColor: 'var(--bg-subtle-cream)' }}
          >
            <div className="stripe-geometric-bg">
              <div className="stripe-shape stripe-shape-1"></div>
              <div className="stripe-shape stripe-shape-2"></div>
              <div className="stripe-shape stripe-shape-3"></div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 relative z-10">
              <div className="flex flex-col md:flex-row items-center gap-12">
                <motion.div className="w-full md:w-1/2" variants={itemVariants}>
                  <img
                    src={require('./assets/customer.jpeg')}
                    alt="AI Agent assisting user"
                    className="rounded-xl shadow-2xl w-full h-auto object-cover"
                  />
                </motion.div>
                <motion.div className="w-full md:w-1/2" variants={itemVariants}>
                  <span className="text-[var(--gradient-text-from)] font-semibold">Autonomous Operation</span>
                  <h3 className="text-3xl sm:text-4xl font-bold my-4 text-[var(--text-color)]">The Brain For Your Computer</h3>
                  <p className="text-lg text-[var(--text-color-secondary)] leading-relaxed mb-6">
                    QueenDahyun is more than a tool—it's an end-to-end autonomous AI. Delegate complex tasks, from coding and research to data analysis, and let it work for you.
                  </p>
                    <a
                      href="https://youtu.be/pLb28qiic1E"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black py-3 px-6 rounded-full text-md font-semibold glow-button shadow-lg inline-block"
                    >
                      See The Demo
                    </a>
                </motion.div>
              </div>
            </div>
          </AnimatedSection>

          <LogoScroller />

          {/* REDESIGNED Early Adopters Section with Geometric Designs */}
          <AnimatedSection id="clients" className="section-transition" style={{ backgroundColor: 'var(--bg-subtle-pink)' }}>
            <div className="container mx-auto px-4 sm:px-6">
              <motion.h2 variants={itemVariants} className="text-4xl sm:text-5xl font-bold text-center mb-6 gradient-text-flow">
                Early Adopters
              </motion.h2>
              <motion.p variants={itemVariants} className="text-center text-[var(--text-color-secondary)] text-lg mb-16 max-w-3xl mx-auto">
                Join the pioneers who are already experiencing the future of autonomous computing
              </motion.p>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {/* Testimonial Card 1 */}
                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 rounded-xl shadow-xl relative overflow-hidden">
                  <div className="geometric-bg">
                    <div className="geometric-shape w-32 h-32 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full absolute -top-10 -right-10 floating"></div>
                    <div className="geometric-shape w-20 h-20 bg-gradient-to-br from-lime-500/20 to-green-500/20 absolute bottom-10 left-5" style={{clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'}}></div>
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-lg mr-3">
                        D
                      </div>
                      <div>
                        <h4 className="font-semibold text-[var(--text-color)]">Dr. Sarah Chen</h4>
                        <p className="text-sm text-[var(--text-color-secondary)]">AI Researcher</p>
                      </div>
                    </div>
                    <p className="text-[var(--text-color-secondary)] leading-relaxed italic">
                      "The vision-language capabilities of SoulDubu are unprecedented. It's like having a PhD-level assistant that never sleeps and can navigate any software with human-like intuition."
                    </p>
                  </div>
                </motion.div>

                {/* Testimonial Card 2 */}
                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 rounded-xl shadow-xl relative overflow-hidden">
                  <div className="geometric-bg">
                    <div className="geometric-shape w-24 h-24 bg-gradient-to-br from-pink-500/20 to-purple-500/20 absolute top-5 right-5" style={{clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'}}></div>
                    <div className="geometric-shape w-16 h-16 border-4 border-lime-500/30 absolute bottom-8 -left-8 floating" style={{borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%'}}></div>
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-lime-400 to-green-500 flex items-center justify-center text-white font-bold text-lg mr-3">
                        M
                      </div>
                      <div>
                        <h4 className="font-semibold text-[var(--text-color)]">Marcus Rivera</h4>
                        <p className="text-sm text-[var(--text-color-secondary)]">Software Architect</p>
                      </div>
                    </div>
                    <p className="text-[var(--text-color-secondary)] leading-relaxed italic">
                      "Running everything locally without cloud dependency is a game-changer for our enterprise. The security model gives us complete confidence in deployment."
                    </p>
                  </div>
                </motion.div>

                {/* Testimonial Card 3 */}
                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 rounded-xl shadow-xl relative overflow-hidden">
                  <div className="geometric-bg">
                    <div className="geometric-shape w-28 h-28 bg-gradient-to-br from-orange-500/20 to-red-500/20 absolute -bottom-5 -right-5" style={{clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'}}></div>
                    <div className="geometric-shape w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-teal-500/20 rounded-lg absolute top-8 left-8 floating"></div>
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold text-lg mr-3">
                        A
                      </div>
                      <div>
                        <h4 className="font-semibold text-[var(--text-color)]">Aisha Patel</h4>
                        <p className="text-sm text-[var(--text-color-secondary)]">Data Scientist</p>
                      </div>
                    </div>
                    <p className="text-[var(--text-color-secondary)] leading-relaxed italic">
                      "From data preprocessing to visualization, QueenDahyun handles complex workflows autonomously. It's like having a senior data scientist working 24/7."
                    </p>
                  </div>
                </motion.div>
              </div>

              {/* Stats Section with Geometric Background */}
              <motion.div variants={itemVariants} className="glass-card p-12 rounded-2xl shadow-2xl relative overflow-hidden">
                <div className="geometric-bg">
                  <div className="geometric-shape w-40 h-40 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-full absolute top-0 right-1/4 floating"></div>
                  <div className="geometric-shape w-32 h-32 bg-gradient-to-br from-lime-500/10 to-green-500/10 absolute bottom-0 left-1/3" style={{clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)'}}></div>
                  <div className="geometric-shape w-24 h-24 border-4 border-pink-500/20 absolute top-1/2 left-10" style={{borderRadius: '63% 37% 54% 46% / 55% 48% 52% 45%'}}></div>
                  <div className="geometric-shape w-36 h-36 bg-gradient-to-br from-purple-500/10 to-pink-500/10 absolute bottom-10 right-10" style={{clipPath: 'polygon(50% 0%, 90% 20%, 100% 60%, 75% 100%, 25% 100%, 0% 60%, 10% 20%)'}}></div>
                </div>

                <div className="relative z-10">
                  <h3 className="text-3xl font-bold text-center mb-12 text-[var(--text-color)]">Join the Revolution</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                    <div className="text-center">
                      <div className="text-4xl md:text-5xl font-black gradient-text-flow mb-2">100+</div>
                      <div className="text-[var(--text-color-secondary)]">Beta Testers</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl md:text-5xl font-black gradient-text-flow mb-2">87.8%</div>
                      <div className="text-[var(--text-color-secondary)]">Satisfaction Rate</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl md:text-5xl font-black gradient-text-flow mb-2">10k+</div>
                      <div className="text-[var(--text-color-secondary)]">Tasks Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-4xl md:text-5xl font-black gradient-text-flow mb-2">100%</div>
                      <div className="text-[var(--text-color-secondary)]">Secure & Private + Local</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </AnimatedSection>

          {/* Use Cases Section */}
          <AnimatedSection id="use-cases" className="section-transition" style={{ backgroundColor: 'var(--bg-main)' }}>
            <div className="container mx-auto px-4 sm:px-6">
              <motion.h2 variants={itemVariants} className="text-4xl sm:text-5xl font-bold text-center mb-6 gradient-text-flow">
                Limitless Applications
              </motion.h2>
              <motion.p variants={itemVariants} className="text-center text-[var(--text-color-secondary)] text-lg mb-16 max-w-3xl mx-auto">
                From research labs to enterprise deployments, QueenDahyun transforms how professionals work
              </motion.p>

              <div className="grid md:grid-cols-2 gap-8">
                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 rounded-xl">
                  <Code className="text-[var(--gradient-text-from)] mb-4" size={36}/>
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-3">Software Development</h3>
                  <p className="text-[var(--text-color-secondary)] leading-relaxed mb-4">
                    From writing boilerplate code to debugging complex systems, refactoring legacy codebases to generating unit tests—SoulDubu understands your IDE, version control, and development workflow.
                  </p>
                  <ul className="space-y-2 text-[var(--text-color-secondary)]">
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--brand-color-lime)]" size={16}/> Automated code generation and refactoring</li>
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--brand-color-lime)]" size={16}/> Intelligent debugging and error resolution</li>
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--brand-color-lime)]" size={16}/> Documentation and test suite generation</li>
                  </ul>
                </motion.div>

                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 rounded-xl">
                  <Database className="text-[var(--brand-color-lime)] mb-4" size={36}/>
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-3">Data Science & Analytics</h3>
                  <p className="text-[var(--text-color-secondary)] leading-relaxed mb-4">
                    Navigate Jupyter notebooks, clean datasets, build pipelines, train models, and generate visualizations—all through natural language instructions to your AI partner.
                  </p>
                  <ul className="space-y-2 text-[var(--text-color-secondary)]">
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--brand-color-lime)]" size={16}/> Automated data cleaning and preprocessing</li>
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--brand-color-lime)]" size={16}/> Model training and hyperparameter tuning</li>
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--brand-color-lime)]" size={16}/> Interactive visualization generation</li>
                  </ul>
                </motion.div>

                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 rounded-xl">
                  <Globe className="text-pink-500 mb-4" size={36}/>
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-3">Research & Academia</h3>
                  <p className="text-[var(--text-color-secondary)] leading-relaxed mb-4">
                    Literature reviews, experiment automation, data analysis, and paper writing assistance—accelerate your research with an AI that understands scientific software and methodologies.
                  </p>
                  <ul className="space-y-2 text-[var(--text-color-secondary)]">
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-pink-500" size={16}/> Automated literature review and synthesis</li>
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-pink-500" size={16}/> Experiment design and execution</li>
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-pink-500" size={16}/> Statistical analysis and visualization</li>
                  </ul>
                </motion.div>

                <motion.div variants={itemVariants} className="glass-card aws-hover-effect p-8 rounded-xl">
                  <Users className="text-[var(--gradient-text-from)] mb-4" size={36}/>
                  <h3 className="text-2xl font-bold text-[var(--text-color)] mb-3">Enterprise Automation</h3>
                  <p className="text-[var(--text-color-secondary)] leading-relaxed mb-4">
                    Automate repetitive workflows, integrate disparate systems, generate reports, and manage complex business processes—all while maintaining enterprise-grade security and auditability.
                  </p>
                  <ul className="space-y-2 text-[var(--text-color-secondary)]">
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--gradient-text-from)]" size={16}/> Workflow automation and optimization</li>
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--gradient-text-from)]" size={16}/> Cross-application data integration</li>
                    <li className="flex items-start"><CheckCircle className="mr-2 mt-1 flex-shrink-0 text-[var(--gradient-text-from)]" size={16}/> Automated reporting and compliance</li>
                  </ul>
                </motion.div>
              </div>
            </div>
          </AnimatedSection>

          {/* Download Section */}
          <AnimatedSection id="download" className="section-transition" style={{ backgroundColor: 'var(--bg-off-white)' }}>
            <div className="container mx-auto px-4 sm:px-6 text-center">
              <motion.h2 variants={itemVariants} className="text-4xl sm:text-5xl font-bold mb-6 gradient-text-flow">
                Download For Your OS
              </motion.h2>
              <motion.p variants={itemVariants} className="text-[var(--text-color-secondary)] text-lg mb-12 max-w-2xl mx-auto">
                Experience the future of autonomous computing. Available for Windows and macOS, with Linux support coming soon.
              </motion.p>
              <motion.div
                className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8"
                variants={itemVariants}
              >
                {[
                  { 
                    os: 'Windows', 
                    description: 'Get the latest version for Windows',
                    available: true
                  },
                  { 
                    os: 'macOS', 
                    description: 'We\'re fixing a bug for this platform. Thank you for your patience!',
                    available: false
                  }
                ].map((item) => (
                  <motion.div
                    key={item.os}
                    className="glass-card aws-hover-effect p-8 rounded-xl shadow-lg flex flex-col items-center text-center"
                    variants={itemVariants}
                  >
                    <Terminal size={48} className="text-[var(--gradient-text-from)] mb-4"/>
                    <h3 className="text-2xl font-semibold mb-2 text-[var(--text-color)]">{item.os}</h3>
                    <p className="text-[var(--text-color-secondary)] text-sm mb-4 flex-grow">{item.description}</p>
                    {item.available ? (
                      <motion.button
                        onClick={() => setShowGPUModal(true)}
                        className="mt-auto text-black py-3 px-8 rounded-full text-md font-semibold glow-button shadow-lg flex items-center justify-center"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Download className="mr-2" size={20} />
                        Download
                      </motion.button>
                    ) : (
                      <span className="mt-auto text-gray-500 py-3 px-8 rounded-full text-md font-semibold shadow-lg flex items-center justify-center cursor-not-allowed opacity-60">
                        <Download className="mr-2" size={20} />
                        Coming Soon
                      </span>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </AnimatedSection>


          {/* GPU Selection Modal */}
          <AnimatePresence>
            {showGPUModal && (
              <GPUSelectionModal 
                isOpen={showGPUModal} 
                onClose={() => setShowGPUModal(false)}
                onGPUSelected={(gpu) => {
                  console.log('Selected GPU:', gpu);
                  setShowGPUModal(false);
                }}
              />
            )}
          </AnimatePresence>

          {/* NEW: Minimalist History Section */}
          <AnimatedSection id="history" className="section-transition" style={{ backgroundColor: 'var(--bg-main)' }}>
            <div className="container mx-auto px-4 sm:px-6">
              <motion.div variants={itemVariants} className="max-w-2xl mx-auto text-center glass-card p-8 rounded-xl">
                <p className="text-lg text-[var(--text-color-secondary)] mb-4">
                  Curious about the story behind the name?
                </p>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); /* Placeholder for alert replacement */ }}
                  className="inline-flex items-center font-semibold text-[var(--gradient-text-from)] hover:text-[var(--gradient-text-to)] transition-colors text-lg"
                >
                  Learn about our inspiration and history
                  <ArrowRight className="ml-2" size={20} />
                </a>
              </motion.div>
            </div>
          </AnimatedSection>

          {/* Footer */}
          <footer className="bg-[var(--footer-bg)] text-[var(--footer-text)] py-16 md:py-20 mt-20 rounded-t-2xl">
            <div className="container mx-auto px-4 sm:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10 md:gap-16 mb-10 md:mb-12">
                <div className="col-span-2 md:col-span-4 lg:col-span-1">
                   <h3 className="text-2xl font-bold text-white mb-4">QueenDahyun AGI</h3>
                   <p className="text-sm">The State of the Art Autonomous AI Agent.</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-white">Useful Links</h3>
                  <ul className="space-y-2">
                    {["Content",  "Create", "Explore", "Terms & Services"].map(link => (
                       <li key={link}><a href="#" className="hover:text-white transition duration-300 text-sm">{link}</a></li>
                    ))}

                  <li><a href="https://youtu.be/pLb28qiic1E" target="_blank" rel="noopener noreferrer" className="hover:text-white transition duration-300 text-sm">How it Works</a></li>
                  </ul>
                </div>
                 <div>
                  <h3 className="text-lg font-semibold mb-4 text-white">Community</h3>
                  <ul className="space-y-2">
                    {["Help Center", "Partners", "Suggestions", "Newsletters"].map(link => (
                       <li key={link}><a href="#" className="hover:text-white transition duration-300 text-sm">{link}</a></li>
                    ))}
                  <li><a href="/blog" className="hover:text-white transition duration-300 text-sm">Blog</a></li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-white">Partner</h3>
                  <ul className="space-y-2">
                    {["Our Partner", "Become a Partner"].map(link => (
                       <li key={link}><a href="#" className="hover:text-white transition duration-300 text-sm">{link}</a></li>
                    ))}
                  </ul>
                </div>
                 <div>
                  <h3 className="text-lg font-semibold mb-4 text-white">Contact</h3>
                  <ul className="space-y-2">
                    <li><a href="/Team" className="hover:text-white transition duration-300 text-sm">Team</a></li>
                    <li><a href="/research" className="hover:text-white transition duration-300 text-sm">Research</a></li>
                  </ul>
                </div>
              </div>
              <div
                className="border-t border-gray-700 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center text-sm"
              >
                <p className="mb-4 sm:mb-0">
                  Copyright © {new Date().getFullYear()} QueenDahyun AGI. All Rights Reserved.
                </p>
                <div className="flex space-x-6">
                  <a href="https://www.linkedin.com/company/queendahyun" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    <Linkedin size={20} />
                  </a>
                  <a href="https://x.com/queendahyun_agi" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                    <Twitter size={20} />
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
};

export default LandingPage;